import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  onSnapshot, 
  serverTimestamp,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { Notification } from '../types/user';

// 알림 생성 함수
export const createNotification = async (
  userId: string,
  notification: Omit<Notification, 'id' | 'createdAt'>
): Promise<string> => {
  try {
    const notificationsRef = collection(db, `users/${userId}/notifications`);
    const docRef = await addDoc(notificationsRef, {
      ...notification,
      createdAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('알림 생성 중 오류:', error);
    throw error;
  }
};

// 알림 읽음 처리
export const markNotificationAsRead = async (
  userId: string,
  notificationId: string
): Promise<void> => {
  try {
    const notificationRef = doc(db, `users/${userId}/notifications/${notificationId}`);
    await updateDoc(notificationRef, {
      isRead: true,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('알림 읽음 처리 중 오류:', error);
    throw error;
  }
};

// 모든 알림 읽음 처리
export const markAllNotificationsAsRead = async (userId: string): Promise<void> => {
  try {
    const notificationsRef = collection(db, `users/${userId}/notifications`);
    const q = query(notificationsRef, where('isRead', '==', false));
    const snapshot = await getDocs(q);
    
    const batch = writeBatch(db);
    snapshot.docs.forEach(doc => {
      batch.update(doc.ref, {
        isRead: true,
        updatedAt: serverTimestamp()
      });
    });
    
    await batch.commit();
  } catch (error) {
    console.error('모든 알림 읽음 처리 중 오류:', error);
    throw error;
  }
};

// 알림 삭제
export const deleteNotification = async (
  userId: string,
  notificationId: string
): Promise<void> => {
  try {
    const notificationRef = doc(db, `users/${userId}/notifications/${notificationId}`);
    await deleteDoc(notificationRef);
  } catch (error) {
    console.error('알림 삭제 중 오류:', error);
    throw error;
  }
};

// 선택한 알림 여러 개 삭제 (배치는 500건씩)
export const deleteNotifications = async (
  userId: string,
  notificationIds: string[]
): Promise<void> => {
  try {
    for (let i = 0; i < notificationIds.length; i += 500) {
      const batch = writeBatch(db);
      notificationIds.slice(i, i + 500).forEach(id => {
        batch.delete(doc(db, `users/${userId}/notifications/${id}`));
      });
      await batch.commit();
    }
  } catch (error) {
    console.error('선택한 알림 삭제 중 오류:', error);
    throw error;
  }
};

// 모든 알림 삭제 (화면에 보이지 않는 오래된 알림까지 지운다. 배치는 500건씩)
export const deleteAllNotifications = async (userId: string): Promise<number> => {
  try {
    const notificationsRef = collection(db, `users/${userId}/notifications`);
    const snapshot = await getDocs(notificationsRef);

    for (let i = 0; i < snapshot.docs.length; i += 500) {
      const batch = writeBatch(db);
      snapshot.docs.slice(i, i + 500).forEach(doc => batch.delete(doc.ref));
      await batch.commit();
    }
    return snapshot.size;
  } catch (error) {
    console.error('모든 알림 삭제 중 오류:', error);
    throw error;
  }
};

// 사용자 알림 조회
export const getUserNotifications = async (
  userId: string,
  limitCount: number = 50
): Promise<Notification[]> => {
  try {
    const notificationsRef = collection(db, `users/${userId}/notifications`);
    const q = query(
      notificationsRef,
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    })) as Notification[];
  } catch (error) {
    console.error('알림 조회 중 오류:', error);
    throw error;
  }
};

// 실시간 알림 구독
export const subscribeToUserNotifications = (
  userId: string,
  callback: (notifications: Notification[]) => void,
  limitCount: number = 50,
  onError?: (error: Error) => void
) => {
  const notificationsRef = collection(db, `users/${userId}/notifications`);
  const q = query(
    notificationsRef,
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  
  return onSnapshot(q, (snapshot) => {
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    })) as Notification[];
    callback(notifications);
  }, (error) => {
    console.error('알림 구독 중 오류:', error);
    onError?.(error);
  });
};

// 읽지 않은 알림 개수 조회
export const getUnreadNotificationCount = async (userId: string): Promise<number> => {
  try {
    const notificationsRef = collection(db, `users/${userId}/notifications`);
    const q = query(notificationsRef, where('isRead', '==', false));
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('읽지 않은 알림 개수 조회 중 오류:', error);
    return 0;
  }
};

// 특정 타입의 알림만 조회
export const getUserNotificationsByType = async (
  userId: string,
  type: Notification['type'],
  limitCount: number = 30
): Promise<Notification[]> => {
  try {
    const notificationsRef = collection(db, `users/${userId}/notifications`);
    const q = query(
      notificationsRef,
      where('type', '==', type),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    })) as Notification[];
  } catch (error) {
    console.error('타입별 알림 조회 중 오류:', error);
    throw error;
  }
};

// 포인트 관련 알림 생성 헬퍼
export const createPointNotification = async (
  userId: string,
  points: number,
  reason: string,
  senderId: string,
  senderName: string,
  schoolId: string,
  classId?: string
): Promise<string> => {
  const notification: Omit<Notification, 'id' | 'createdAt'> = {
    type: 'point',
    title: points > 0 ? '포인트 지급' : '포인트 차감',
    message: `${Math.abs(points)}점이 ${points > 0 ? '지급' : '차감'}되었습니다. (${reason})`,
    priority: 'medium',
    isRead: false,
    actionUrl: '/class/student/PointHistory',
    senderId,
    senderName,
    schoolId,
    classId,
    metadata: {
      points,
      reason
    }
  };
  
  return await createNotification(userId, notification);
};

// 아이템 승인 요청 알림 생성 헬퍼
export const createApprovalNotification = async (
  teacherId: string,
  studentName: string,
  itemName: string,
  requestId: string,
  schoolId: string,
  classId: string,
  studentGrade?: number,
  studentClass?: number
): Promise<string> => {
  const classLabel = studentGrade !== undefined && studentClass !== undefined
    ? `${studentGrade}학년 ${studentClass}반 `
    : '';
  const notification: Omit<Notification, 'id' | 'createdAt'> = {
    type: 'approval',
    title: '아이템 사용 승인 요청',
    message: `${classLabel}${studentName} 학생이 ${itemName} 사용 승인을 요청했습니다.`,
    priority: 'high',
    isRead: false,
    actionUrl: `/class/teacher/ItemApproval`,
    schoolId,
    classId,
    metadata: {
      studentName,
      itemName,
      requestId,
      studentGrade,
      studentClass
    }
  };
  
  return await createNotification(teacherId, notification);
};

// 역할 관련 알림 생성 헬퍼
export const createRoleNotification = async (
  userId: string,
  title: string,
  message: string,
  schoolId: string,
  classId?: string,
  priority: Notification['priority'] = 'medium',
  actionUrl?: string,
  metadata?: Notification['metadata']
): Promise<string> => {
  const notification: Omit<Notification, 'id' | 'createdAt'> = {
    type: 'role',
    title,
    message,
    priority,
    isRead: false,
    actionUrl: actionUrl || '/class/teacher/ClassRoleManagement',
    schoolId,
    classId,
    metadata
  };
  
  return await createNotification(userId, notification);
};

// 아이템 관련 알림 생성 헬퍼
export const createItemNotification = async (
  userId: string,
  title: string,
  message: string,
  schoolId: string,
  classId?: string,
  priority: Notification['priority'] = 'medium',
  actionUrl?: string,
  metadata?: Notification['metadata']
): Promise<string> => {
  const notification: Omit<Notification, 'id' | 'createdAt'> = {
    type: 'item',
    title,
    message,
    priority,
    isRead: false,
    actionUrl,
    schoolId,
    classId,
    metadata
  };
  
  return await createNotification(userId, notification);
};

// 일반 알림 생성 헬퍼
export const createGeneralNotification = async (
  userId: string,
  title: string,
  message: string,
  priority: Notification['priority'] = 'medium',
  actionUrl?: string,
  metadata?: Notification['metadata']
): Promise<string> => {
  const notification: Omit<Notification, 'id' | 'createdAt'> = {
    type: 'general',
    title,
    message,
    priority,
    isRead: false,
    actionUrl,
    metadata
  };
  
  return await createNotification(userId, notification);
};

// 시스템 알림 생성 헬퍼
export const createSystemNotification = async (
  userId: string,
  title: string,
  message: string,
  priority: Notification['priority'] = 'medium',
  actionUrl?: string
): Promise<string> => {
  const notification: Omit<Notification, 'id' | 'createdAt'> = {
    type: 'system',
    title,
    message,
    priority,
    isRead: false,
    actionUrl
  };
  
  return await createNotification(userId, notification);
};

// 알림 타입별 표시 정보 반환 헬퍼 함수들
export const getNotificationTypeColor = (type: Notification['type']): string => {
  const colorMap: Record<Notification['type'], string> = {
    'point': '#4caf50',      // 초록색
    'item': '#9c27b0',       // 보라색
    'role': '#ff9800',       // 주황색
    'system': '#2196f3',     // 파란색
    'general': '#9e9e9e',    // 회색
    'approval': '#ff9800'    // 주황색 (승인 요청)
  };
  return colorMap[type] || '#9e9e9e';
};

export const getNotificationTypeIcon = (type: Notification['type']): string => {
  const iconMap: Record<Notification['type'], string> = {
    'point': '💰',
    'item': '📦',
    'role': '👤',
    'system': 'ℹ️',
    'general': '📢',
    'approval': '✅'
  };
  return iconMap[type] || '📢';
};

export const getNotificationBackgroundColor = (notification: Notification): string => {
  if (notification.isRead) {
    return '#f9f9f9';
  }
  
  const backgroundColorMap: Record<Notification['type'], string> = {
    'point': '#e8f5e9',      // 연한 초록색
    'item': '#f3e5f5',       // 연한 보라색
    'role': '#fff3e0',       // 연한 주황색
    'system': '#e3f2fd',     // 연한 파란색
    'general': '#f5f5f5',    // 연한 회색
    'approval': '#fff3e0'    // 연한 주황색
  };
  
  return backgroundColorMap[notification.type] || '#f5f5f5';
};



